import angularModule from 'Careers/angularModule/scripts/careers_module';
import template from 'Careers/angularModule/views/edit_career_profile/tuition_and_scholarships_form.html';
import cacheAngularTemplate from 'cacheAngularTemplate';
import { SHORT_ANSWER_SCHOLARSHIP_MAX_CHARS, SHORT_ANSWER_SCHOLARSHIP_MIN_CHARS } from 'FormConfigs';
import scholarshipConfigs from 'Scholarship/ScholarshipConfigs';
import { ScholarshipCategory } from 'Scholarship';
import { setupBrandScopeProperties } from 'AppBranding';
import { getCohort } from 'Users';
import { ProgramTypeConfigs } from 'Program';

const templateUrl = cacheAngularTemplate(angularModule, template);
const stepName = 'tuition_and_scholarships';

angularModule.directive('tuitionAndScholarshipsForm', [
    '$injector',
    function factory($injector) {
        const FormHelper = $injector.get('FormHelper');
        const EditCareerProfileHelper = $injector.get('EditCareerProfileHelper');
        const DialogModal = $injector.get('DialogModal');
        const TranslationHelper = $injector.get('TranslationHelper');
        const translationHelper = new TranslationHelper('careers.edit_career_profile.tuition_and_scholarships_form');
        const slTranslationHelper = new TranslationHelper('scholarship_labels');

        return {
            restrict: 'E',
            // see STEP_FORM_ATTRS in edit_career_profile_dir.js
            scope: {
                // form-step-validity reads this by name off the scope. See STEP_FORM_ATTRS.
                $setValidity: '<setValidity',
                currentUser: '<',
                formContext: '<',
                programFamilyFormDatum: '<',
                programType: '<',
                onSave: '&',
                onSaveAndNext: '&',
                transientFormValues: '<',
                setDisableSaveForStep: '&',
                setSaveClickInterceptorForStep: '&',
                setSaveNextClickInterceptorForStep: '&',
            },
            templateUrl,
            link(scope) {
                // Setup
                FormHelper.supportForm(scope, scope.tuition_and_scholarships);

                // Tell the schema that the tuition inputs are on the page, so it applies the rules for
                // those inputs rather than the rules for saved values. See TUITION_STEP_ON_SCREEN in
                // FormConfigs.
                scope.transientFormValues.tuition_step_on_screen = true;

                // Set initial scope values
                Object.defineProperty(scope, 'programTypeConfig', {
                    get() {
                        const cohort = getCohort(scope.currentUser);
                        return ProgramTypeConfigs[cohort?.programType];
                    },
                    configurable: true,
                });

                setupBrandScopeProperties($injector, scope, ['brandNameShort']);
                scope.showScholarshipOptions = false;

                // Set 'Save' button interceptor to show confirmation modal if no scholarships selected
                scope.setSaveClickInterceptorForStep({
                    stepName,
                    interceptor: () => scope.showAppropriateModalIfNecessary('save'),
                });

                // Set 'Save and Next' button interceptor to show confirmation modal if no scholarships selected
                scope.setSaveNextClickInterceptorForStep({
                    stepName,
                    interceptor: () => scope.showAppropriateModalIfNecessary('saveAndNext'),
                });

                // Watch radio button selection
                scope.$watch('programFamilyFormDatum.declineScholarshipConsideration', val => {
                    const shouldDisable = [null, undefined].includes(val);
                    scope.setDisableSaveForStep({ stepName, shouldDisable });
                    scope.showScholarshipOptions = val === false;
                });

                // Watch scholarship selections to unset need-specific fields when need is unchecked
                scope.$watchCollection('programFamilyFormDatum.scholarshipLabelsInterestedIn', (newVal, oldVal) => {
                    if (oldVal.includes('need') && !newVal.includes('need')) {
                        scope.programFamilyFormDatum.shortAnswerScholarship = null;
                        scope.programFamilyFormDatum.requestFullScholarship = false;
                    }
                });

                scope.shortAnswerMinChars = SHORT_ANSWER_SCHOLARSHIP_MIN_CHARS;
                scope.shortAnswerMaxChars = SHORT_ANSWER_SCHOLARSHIP_MAX_CHARS;

                EditCareerProfileHelper.logEventForApplication(scope.formContext, 'viewed-tuition_and_scholarships');

                // Support scholarship checkboxes
                const scholarshipValues = Object.values(ScholarshipCategory);
                const propsValuesMap = { scholarshipLabelsInterestedIn: scholarshipValues };
                const propsRequiredFieldsMap = {
                    declineScholarshipConsideration: true,
                };
                const propsDefaultAndDominantValuesMap = {
                    scholarshipLabelsInterestedIn: scope.programFamilyFormDatum.scholarshipLabelsInterestedIn || [],
                };

                FormHelper.supportCheckboxGroups(
                    scope,
                    scope.programFamilyFormDatum,
                    propsValuesMap,
                    propsRequiredFieldsMap,
                    propsDefaultAndDominantValuesMap,
                );

                // If reapplying, a user could have been previously interested in a scholarship we no longer
                // have, so we make sure to filter out values not in ScholarshipCategory
                const currentInterests = scope.programFamilyFormDatum.scholarshipLabelsInterestedIn;
                if (currentInterests?.length) {
                    scope.programFamilyFormDatum.scholarshipLabelsInterestedIn = currentInterests.filter(val =>
                        scholarshipValues.includes(val),
                    );
                }

                // See https://trello.com/c/hlnc7NBu. A user invited to a fellowship via an InMail
                // campaign sees the fellowship in the merit slot, an invitation line, and the
                // "Apply for Awards" radio pre-selected with the merit option pre-checked. Users
                // with no matching invitation keep today's behavior: default merit copy and an
                // unselected radio group they must answer themselves.
                const preselectFellowship = () => {
                    const datum = scope.programFamilyFormDatum;
                    // Never clobber an answer the user already gave (or previously saved).
                    const unanswered =
                        [null, undefined].includes(datum.declineScholarshipConsideration) &&
                        !datum.scholarshipLabelsInterestedIn?.length;
                    if (!unanswered) return;

                    datum.declineScholarshipConsideration = false;
                    scope.toggleCheckbox('scholarshipLabelsInterestedIn', ScholarshipCategory.merit);
                };

                let destroyed = false;
                scope.$on('$destroy', () => {
                    destroyed = true;
                });

                // Build data for form and form control
                const resolveScholarshipConfigs = async programType => {
                    const configs = await scholarshipConfigs($injector, 'application', programType);
                    // The await above can take up to 10 seconds on a cold Statsig load, and Angular
                    // never cancels it. If the user leaves the step before it settles, bail out:
                    // preselectFellowship mutates programFamilyFormDatum, which the parent editor
                    // owns, so a late resolution would silently change data the user could save later.
                    if (destroyed) return;

                    scope.$applyAsync(() => {
                        configs.need.label = slTranslationHelper.get('regional_application');
                        scope.scholarshipConfigs = { need: configs.need, merit: configs.merit };

                        if (configs.merit.isFellowshipInvitation) {
                            scope.invitationLine = translationHelper.get('invited_to_apply', {
                                fellowshipLabel: configs.merit.label,
                            });
                            preselectFellowship();
                        } else {
                            // Revert the copy only. Never un-check boxes; the user may
                            // have touched them.
                            scope.invitationLine = null;
                        }
                    });
                };

                // Fires on link with the initial program type and again whenever the
                // "Change Program" link switches the relevant cohort.
                scope.$watch(() => getCohort(scope.currentUser)?.programType, resolveScholarshipConfigs);

                scope.unsetScholarshipSelections = () => {
                    DialogModal.hideAlerts();
                    scope.checkboxGroupsFauxModels.scholarshipLabelsInterestedIn = [];
                    scope.programFamilyFormDatum.scholarshipLabelsInterestedIn = [];
                    scope.programFamilyFormDatum.shortAnswerScholarship = null;
                    scope.programFamilyFormDatum.requestFullScholarship = false;
                    scope.programFamilyFormDatum.declineScholarshipConsideration = true;
                    scope.setDisableSaveForStep({ stepName, shouldDisable: false });
                };

                /** MODALS  */
                scope.showAppropriateModalIfNecessary = action => {
                    if (scope.programFamilyFormDatum.declineScholarshipConsideration) {
                        scope.unsetScholarshipSelections();
                        scope.executeSaveAction(action);
                    } else if (
                        !scope.programFamilyFormDatum.declineScholarshipConsideration &&
                        !scope.programFamilyFormDatum.scholarshipLabelsInterestedIn.length
                    ) {
                        scope.showNoSelectionModal(action);
                    } else {
                        scope.executeSaveAction(action);
                    }
                };

                scope.executeSaveAction = action => {
                    if (action === 'saveAndNext') {
                        scope.onSaveAndNext();
                    } else {
                        scope.onSave();
                    }
                };

                /** The modal that shows if the user views the scholarships, selects none, and then saves */
                scope.showNoSelectionModal = action => {
                    DialogModal.alert({
                        classes: ['decline-scholarships-modal'],
                        content: `
                            <div class="decline-modal-body">
                                ${translationHelper.get('post_decline_modal_body')}

                                <div class="decline-modal-action-buttons">
                                    <button class="confirm-button btn flat blue" ng-click="handleClickConfirmDeclineScholarship()">
                                        ${translationHelper.get('confirm')}
                                    </button>

                                    <button class="close-and-review-button" ng-click="handleClickCloseAndReview()">
                                        ${translationHelper.get('post_decline_modal_close_and_select')}
                                    </button>
                                </div>
                            </div>
                        `,
                        title: '',
                        size: 'normal',
                        scope: {
                            handleClickCloseAndReview: scope.handleClickCloseAndReview,
                            handleClickConfirmDeclineScholarship: () => {
                                scope.unsetScholarshipSelections();
                                scope.executeSaveAction(action);
                            },
                        },
                    });
                };

                // Handler for the modal dismiss link
                scope.handleClickCloseAndReview = () => {
                    DialogModal.hideAlerts();
                    scope.programFamilyFormDatum.declineScholarshipConsideration = false;
                    scope.showScholarshipOptions = true;
                    scope.setDisableSaveForStep({ stepName, shouldDisable: false });
                };
            },
        };
    },
]);
